import React, { useEffect, useMemo, useRef, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { getLoginConfig, login } from '@/services/api';
import styles from './LoginForm.module.css';
import { getClientIdFromReturnUrl } from '@/lib/clientId';
import { SPA_ROUTES } from '@/lib/paths';
import { Link, useNavigate } from 'react-router-dom';

type FormValues = { email: string; password: string; rememberMe: boolean };

type LoginConfig = { federatedLoginEnabled: boolean };

export function LoginForm({ returnUrl }: { returnUrl?: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
    getValues,
    resetField,
  } = useForm<FormValues>({
    defaultValues: { email: '', password: '', rememberMe: false },
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  const emailInputRef = useRef<HTMLInputElement | null>(null);
  const passwordInputRef = useRef<HTMLInputElement | null>(null);

  const [emailFilled, setEmailFilled] = useState(false);
  const [passwordFilled, setPasswordFilled] = useState(false);

  const syncFilled = () => {
    setEmailFilled(Boolean(emailInputRef.current?.value));
    setPasswordFilled(Boolean(passwordInputRef.current?.value));
  };

  useEffect(() => {
    let cancelled = false;
    let tries = 0;

    const tick = () => {
      if (cancelled) return;
      syncFilled();
      tries += 1;

      if (tries < 30) {
        window.setTimeout(tick, 200);
      }
    };

    tick();

    const onPageShow = () => syncFilled();
    const onFocus = () => syncFilled();
    const onVisibilityChange = () => {
      if (!document.hidden) syncFilled();
    };

    window.addEventListener('pageshow', onPageShow);
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      cancelled = true;
      window.removeEventListener('pageshow', onPageShow);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  const emailReg = register('email', { required: 'Required_attribute_error' });
  const passwordReg = register('password', { required: 'Required_attribute_error' });

  const returnUrlQuery = useMemo(
    () => (returnUrl ? `?returnUrl=${encodeURIComponent(returnUrl)}` : ''),
    [returnUrl],
  );

  const onInvalid = () => {
    const emailVal = getValues('email');
    if (!emailVal?.trim()) return;

    resetField('password', { keepError: true });
  };

  const onSubmit: SubmitHandler<FormValues> = async (values) => {
    const resp = await login({ ...values, returnUrl });
    if (resp.ok) {
      if (resp.redirectUrl) {
        window.location.assign(resp.redirectUrl);
        return;
      }

      navigate(SPA_ROUTES.MANAGE);
      return;
    }

    if (resp.next === 'Lockout') {
      navigate(SPA_ROUTES.LOCKOUT);
      return;
    }
    if (
      (resp.next === 'ConfirmAccountRequired' ||
        resp.next === 'PasswordResetRequired' ||
        resp.next === 'TwoFactorRequired') &&
      resp.redirectUrl
    ) {
      window.location.assign(resp.redirectUrl);
      return;
    }

    resetField('password');

    if (resp.fieldErrors) {
      const fieldMap: Record<string, keyof FormValues> = {
        email: 'email',
        password: 'password',
        rememberme: 'rememberMe',
      };

      Object.entries(resp.fieldErrors).forEach(([k, v]) => {
        const mapped = fieldMap[k] ?? 'email';
        setError(mapped, { message: v });
      });
    } else {
      setError('email', { message: t('Invalid_login_attempt') });
      resetField('password');
    }
  };

  const [cfg, setCfg] = useState<LoginConfig | null>(null);
  useEffect(() => {
    getLoginConfig()
      .then(setCfg)
      .catch(() => setCfg({ federatedLoginEnabled: false }));
  }, []);

  const onFederated = () => {
    navigate(`${SPA_ROUTES.FEDERATED_LOGIN}${returnUrlQuery}`);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit, onInvalid)} className={styles.form} noValidate>
      {/* EMAIL */}
      <div className={`floatField ${emailFilled ? 'isFilled' : ''}`}>
        <input
          id="login-email"
          {...emailReg}
          ref={(el) => {
            emailReg.ref(el);
            emailInputRef.current = el;
          }}
          autoComplete="username"
          className="floatInput"
          placeholder=" "
          aria-invalid={!!errors.email}
          aria-describedby={errors.email ? 'login-email-error' : undefined}
          onInput={syncFilled}
          onChange={(e) => {
            emailReg.onChange(e);
            syncFilled();
          }}
          onFocus={syncFilled}
          onBlur={(e) => {
            emailReg.onBlur(e);
            syncFilled();
          }}
        />
        <label htmlFor="login-email" className="floatLabel">
          {t('Email')}
        </label>
        {errors.email && (
          <p id="login-email-error" className="fieldError" role="alert">
            {t(errors.email.message ?? 'Invalid_login_attempt', {
              arg0: t('Email'),
            })}
          </p>
        )}
      </div>

      {/* PASSWORD */}
      <div className={`floatField ${passwordFilled ? 'isFilled' : ''}`}>
        <input
          id="login-password"
          type="password"
          {...passwordReg}
          ref={(el) => {
            passwordReg.ref(el);
            passwordInputRef.current = el;
          }}
          autoComplete="current-password"
          className="floatInput"
          placeholder=" "
          aria-invalid={!!errors.password}
          aria-describedby={errors.password ? 'login-password-error' : undefined}
          onInput={syncFilled}
          onChange={(e) => {
            passwordReg.onChange(e);
            syncFilled();
          }}
          onFocus={syncFilled}
          onBlur={(e) => {
            passwordReg.onBlur(e);
            syncFilled();
          }}
        />
        <label htmlFor="login-password" className="floatLabel">
          {t('Password')}
        </label>
        {errors.password && (
          <p id="login-password-error" className="fieldError" role="alert">
            {t(errors.password.message ?? 'Invalid_login_attempt', {
              arg0: t('Password'),
            })}
          </p>
        )}
      </div>

      <HideOnBmStudio returnUrl={returnUrl}>
        <label className={styles.checkbox}>
          <input type="checkbox" {...register('rememberMe')} />
          <span>{t('Keep_me_signed_in')}</span>
        </label>
      </HideOnBmStudio>

      <div>
        <button
          type="submit"
          disabled={isSubmitting}
          className={`btn btn-default ${styles.formButton}`}
        >
          {t('Log_in')}
        </button>
      </div>

      <p className={`${styles.center} ${styles.forgot}`}>
        <Link to={`${SPA_ROUTES.FORGOT_PASSWORD}${returnUrlQuery}`} className="text-link">
          {t('Forgot_password')}
        </Link>
      </p>

      {cfg && cfg?.federatedLoginEnabled && (
        <>
          <div className={styles.divider}>
            <span>{t('OrDivider')}</span>
          </div>

          <p className={styles.center}>
            <button
              type="button"
              onClick={onFederated}
              disabled={isSubmitting}
              className={`btn btn-outline ${styles.formButton}`}
            >
              {t('SsoLogin')}
            </button>
          </p>
        </>
      )}

      <HideOnBmStudio returnUrl={returnUrl}>
        <>
          <hr />
          <p className={`${styles.center} ${styles.muted}`}>
            {t('Dont_have_account')}{' '}
            <Link
              to={`${SPA_ROUTES.REGISTER}${returnUrlQuery}`}
              className={`text-link ${styles.formButton}`}
            >
              {t('Create_account')}
            </Link>
          </p>
        </>
      </HideOnBmStudio>
    </form>
  );
}

function HideOnBmStudio({
  children,
  returnUrl,
}: {
  children: React.ReactNode;
  returnUrl?: string;
}) {
  const clientId = getClientIdFromReturnUrl(returnUrl);
  if (clientId === 'bmstudio') return null;
  return <>{children}</>;
}
